import { useState } from 'react';
import { 
  Calendar, Clock, User, CheckCircle, XCircle, AlertCircle,
  ChevronLeft, ChevronRight, Plus, Trash2, Edit2
} from 'lucide-react';

interface ScheduleEntry {
  id: string;
  staffId: string;
  staffName: string;
  role: string;
  date: string;
  startTime: string;
  endTime: string;
  property?: string;
  task?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
}

export default function StaffSchedule() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);

  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  const getWeekDates = (date: Date) => {
    const week = [];
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay());
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentDate);

  const getEntriesForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return schedule.filter(entry => entry.date === dateStr);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      no_show: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    };
    return colors[status as keyof typeof colors] || colors.scheduled;
  };

  const navigateWeek = (direction: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  if (schedule.length === 0) return (
    <div className="p-6"><div className="rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-10 text-center">
        <p className="font-semibold text-[var(--v-text-primary)]">Sin datos todavía</p>
        <p className="mt-1 text-sm text-[var(--v-text-secondary)]">Esta sección se llenará con información real cuando registres propiedades y actividad.</p>
      </div></div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Horarios de Personal</h2>
          <p className="text-slate-400">Gestiona turnos y asignaciones</p>
        </div>
        <div className="flex gap-2">
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Nueva Asignación
          </button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-white font-semibold">
            {weekDates[0].toLocaleDateString('es-ES', { month: 'long', day: 'numeric' })} - 
            {weekDates[6].toLocaleDateString('es-ES', { month: 'long', day: 'numeric' })}
          </h3>
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('week')}
            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'week' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
          >
            Semana
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'month' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
          >
            Mes
          </button>
        </div>
      </div>

      {/* Week View */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        <div className="grid grid-cols-7 divide-x divide-slate-700">
          {weekDates.map((date, idx) => (
            <div key={idx} className="min-h-[400px]">
              <div className={`p-3 text-center border-b border-[var(--v-surface-3)] ${
                date.toDateString() === new Date().toDateString() ? 'bg-blue-600/20' : ''
              }`}>
                <p className="text-slate-400 text-sm">{weekDays[idx]}</p>
                <p className={`text-xl font-bold ${
                  date.toDateString() === new Date().toDateString() ? 'text-blue-400' : 'text-white'
                }`}>
                  {date.getDate()}
                </p>
              </div>
              <div className="p-2 space-y-2">
                {getEntriesForDate(date).map((entry) => (
                  <div
                    key={entry.id}
                    className={`p-3 rounded-lg border ${getStatusColor(entry.status)} cursor-pointer hover:opacity-80`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-3 h-3" />
                      <span className="text-xs font-medium">{entry.startTime} - {entry.endTime}</span>
                    </div>
                    <p className="font-medium text-sm">{entry.staffName}</p>
                    <p className="text-xs opacity-80">{entry.role}</p>
                    {entry.property && (
                      <p className="text-xs opacity-70 mt-1">{entry.property}</p>
                    )}
                    {entry.task && (
                      <p className="text-xs opacity-70">{entry.task}</p>
                    )}
                  </div>
                ))}
                <button className="w-full py-2 border-2 border-dashed border-[var(--v-surface-3)] rounded-lg text-slate-500 hover:border-slate-600 hover:text-slate-400 text-sm">
                  + Agregar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500/30" />
          <span className="text-slate-400">Programado</span>
        </span>
        <span className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/30" />
          <span className="text-slate-400">Completado</span>
        </span>
        <span className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500/20 border border-red-500/30" />
          <span className="text-slate-400">Cancelado</span>
        </span>
        <span className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500/30" />
          <span className="text-slate-400">No Asistió</span>
        </span>
      </div>
    </div>
  );
}
